// Field tracking — throttled position sharing to positions/{uid}.
// Everyone is visible on the map by default, so the position doc carries the
// agent's codename for labelling. Uses the geo wrapper, so the
// riverghost:fakeGps override applies here too.
import { doc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { watchPosition } from "./geo";

let stopWatch: (() => void) | null = null;
let lastWrite = 0;
let codename = "AGENT";
const THROTTLE_MS = 15000;

/**
 * Begin sharing this agent's position. Writes are throttled to ~15s and tagged
 * with the agent's codename so other agents' maps can label the blip. Calling
 * again while already sharing just refreshes the codename. Also flags the guest
 * doc so the app knows tracking is on. On denial/failure the flag is cleared and
 * onError (if given) fires so the UI can tell the agent.
 */
export function startSharing(
  uid: string,
  cn: string | null,
  onError?: (reason: "denied" | "unavailable") => void,
): void {
  if (cn) codename = cn;

  // Flag the guest; ignore failures (offline etc).
  void updateDoc(doc(db, "guests", uid), { shareLocation: true }).catch(() => {});

  if (stopWatch) return; // already watching

  stopWatch = watchPosition(
    (p) => {
      const now = Date.now();
      if (now - lastWrite < THROTTLE_MS) return;
      lastWrite = now;
      void setDoc(
        doc(db, "positions", uid),
        {
          lat: p.lat,
          lng: p.lng,
          accuracy: p.accuracy ?? 0,
          codename,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      ).catch(() => {});
    },
    (reason) => {
      stopSharing();
      // Reflect reality on the guest doc so the UI doesn't claim tracking is on.
      void updateDoc(doc(db, "guests", uid), { shareLocation: false }).catch(() => {});
      onError?.(reason);
    },
  );
}

export function stopSharing(): void {
  stopWatch?.();
  stopWatch = null;
}
